import { useEffect, useState, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CreditCard, ShieldCheck, CheckCircle2, XCircle, Loader2 } from "lucide-react";

export default function PayPage() {
  const { token } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [invoice, setInvoice] = useState(null);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("idle"); // idle | creating | polling | paid | expired | cancelled
  const [pollMsg, setPollMsg] = useState("");

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/pay/invoice/${token}`);
      setInvoice(r.data);
      if (r.data.status === "paid") setStatus("paid");
    } catch (e) {
      setError(e.response?.data?.detail || "Invoice not found");
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const pollStatus = useCallback(async (sessionId, attempts = 0) => {
    const maxAttempts = 12;
    if (attempts >= maxAttempts) {
      setPollMsg("Still checking… you can safely close this page — you'll receive an email confirmation once the payment clears.");
      return;
    }
    try {
      const r = await api.get(`/pay/status/${sessionId}`);
      if (r.data.payment_status === "paid") {
        setStatus("paid");
        setPollMsg("Payment successful — thank you!");
        return;
      }
      if (r.data.status === "expired") {
        setStatus("expired");
        setPollMsg("Payment session expired. Please try again.");
        return;
      }
      setStatus("polling");
      setPollMsg("Payment is being processed…");
      setTimeout(() => pollStatus(sessionId, attempts + 1), 2500);
    } catch (e) {
      setPollMsg("Couldn't reach payment server. Retrying…");
      setTimeout(() => pollStatus(sessionId, attempts + 1), 2500);
    }
  }, []);

  // Handle return from Stripe: session_id in query string
  useEffect(() => {
    const sid = searchParams.get("session_id");
    if (sid) {
      setStatus("polling");
      pollStatus(sid);
    }
  }, [searchParams, pollStatus]);

  const startCheckout = async () => {
    setStatus("creating");
    setError("");
    try {
      const r = await api.post(`/pay/checkout/${token}`, { origin_url: window.location.origin });
      if (r.data.url) {
        window.location.href = r.data.url;
      } else {
        setStatus("idle");
        setError("Could not start payment session");
      }
    } catch (e) {
      setStatus("idle");
      setError(e.response?.data?.detail || "Payment could not be started");
    }
  };

  if (error && !invoice) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-[rgb(var(--bg))]">
        <div className="card-industrial max-w-md w-full p-8 text-center">
          <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <div className="heading text-xl font-black uppercase mb-2">Link Not Available</div>
          <div className="text-sm text-muted-industrial">{error}</div>
        </div>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-[rgb(var(--bg))]">
        <div className="label-tag text-primary-orange pulse-orange">Loading…</div>
      </div>
    );
  }

  const isPaid = status === "paid" || invoice.status === "paid";

  return (
    <div className="min-h-screen bg-[rgb(var(--bg))] p-4 md:p-10 flex items-center justify-center" data-testid="pay-page">
      <div className="w-full max-w-lg">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="label-tag text-primary-orange">TAX INVOICE</div>
          <div className="heading text-3xl font-black uppercase">{invoice?.brand_name || "ArmorBatch"}</div>
        </div>

        <div className="card-industrial p-6" data-testid="pay-card">
          {isPaid ? (
            <div className="text-center py-4" data-testid="pay-success">
              <CheckCircle2 className="w-16 h-16 text-primary-orange mx-auto mb-3" />
              <div className="heading text-2xl font-black uppercase text-primary-orange">Paid</div>
              <div className="text-sm text-muted-industrial mt-1">Invoice {invoice.invoice_no}</div>
              <div className="mono text-3xl font-bold mt-3">${invoice.total.toFixed(2)}</div>
              <div className="text-xs text-muted-industrial mt-4">A receipt has been emailed to you. You can safely close this page.</div>
            </div>
          ) : status === "polling" ? (
            <div className="text-center py-4" data-testid="pay-polling">
              <Loader2 className="w-12 h-12 text-primary-orange mx-auto mb-3 animate-spin" />
              <div className="heading text-xl font-black uppercase">Processing</div>
              <div className="text-sm text-muted-industrial mt-2">{pollMsg}</div>
            </div>
          ) : (
            <>
              <div className="label-tag">Invoice</div>
              <div className="heading text-2xl font-black">{invoice.invoice_no}</div>
              <div className="text-sm text-muted-industrial">{invoice.customer_name}</div>

              <div className="mt-5 border-l-2 border-primary-orange pl-4 py-2">
                {invoice.lines.map((l, i) => (
                  <div key={i} className="flex justify-between text-xs text-muted-industrial mb-1">
                    <span>{l.description}</span>
                    <span className="mono">${l.amount.toFixed(2)}</span>
                  </div>
                ))}
              </div>

              <div className="mt-4 space-y-1">
                <div className="flex justify-between mono text-xs text-muted-industrial"><span>Subtotal</span><span>${invoice.subtotal.toFixed(2)}</span></div>
                <div className="flex justify-between mono text-xs text-muted-industrial"><span>GST 10%</span><span>${invoice.tax.toFixed(2)}</span></div>
                <div className="flex justify-between mono text-sm font-bold mt-1 pt-1 border-t border-industrial/40">
                  <span>Invoice total</span>
                  <span data-testid="pay-invoice-total">${invoice.total.toFixed(2)}</span>
                </div>
                {invoice.cc_surcharge > 0 && (
                  <div className="flex justify-between mono text-xs text-amber-400" data-testid="pay-surcharge-row">
                    <span>Credit card surcharge {invoice.cc_surcharge_pct}%</span>
                    <span>+ ${invoice.cc_surcharge.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between mono text-2xl font-bold text-primary-orange mt-2 pt-2 border-t border-industrial">
                  <span>PAY TODAY</span>
                  <span data-testid="pay-total">${(invoice.total_with_cc_surcharge ?? invoice.total).toFixed(2)}</span>
                </div>
                <div className="mono text-[10px] uppercase text-muted-industrial text-right">AUD · GST inclusive · 1.1% CC surcharge added</div>
              </div>

              {error && (
                <div className="mt-4 border-l-2 border-red-500 pl-3 py-2 text-xs text-red-400" data-testid="pay-error">{error}</div>
              )}

              <Button
                data-testid="pay-now-btn"
                onClick={startCheckout}
                disabled={status === "creating"}
                className="w-full mt-5 rounded-none bg-primary-orange hover:bg-[rgb(var(--primary-hover))] uppercase tracking-widest text-sm font-bold h-12"
              >
                <CreditCard className="w-5 h-5 mr-2" />
                {status === "creating" ? "Opening secure checkout…" : `Pay $${(invoice.total_with_cc_surcharge ?? invoice.total).toFixed(2)} Now`}
              </Button>

              <div className="mt-4 flex items-center justify-center gap-2 text-[10px] uppercase tracking-widest text-muted-industrial">
                <ShieldCheck className="w-3 h-3 text-primary-orange" /> Secure payment powered by Stripe
              </div>
            </>
          )}
        </div>

        <div className="text-center mt-6 text-[10px] mono uppercase tracking-widest text-muted-industrial">
          {invoice?.brand_name || "ArmorBatch"} · Questions? Reply to your invoice email
        </div>
      </div>
    </div>
  );
}
